// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2022-09-23 13:22:30 +0200 (Fri, 23 Sep 2022) $
//  Author    : Albert Charlier, Dirk Rothe
//  Worker    : $Author: o.powroznik $
//  Revision  : $Rev: 129318 $
//  Purpose   : navigation elements
// =============================================================================
// ---Third-party imports
import React, {Component, Fragment} from 'react'
import {
  Header,
  Divider,
  Icon,
  Menu,
  Item,
  Dropdown,
  Pagination,
  Rail,
  Segment,
  Sticky,
  Loader,
} from 'semantic-ui-react'

// ---Framework imports
import {entries, groupBy, sorted, size, range, has, flatten, keys} from 'ldash'
import {Part, funcPart} from 'uix/parts'
import {Actions} from 'uix/registries'
import log from 'uix/logging'
import connect from 'uix/connect'
import styles from 'uix/styles'
import {Link, LargeThumb} from 'uix/components'
import {Facets} from 'uix/frontend/components'
import {TitleGrid} from 'uix/widgets/titleGrid'
import {formatDate} from 'uix/utils'

// ---Globals
export {ProtoHeader} from './headers'

let BoxHeaderIcon = ({name}) => (
  <Header as="h4">
    <Icon name="folder outline" />
    <Header.Content>{name}</Header.Content>
  </Header>
)

let ListItem = connect(
  'ListItem',
  ['meta.request', 'config.domains'],
  ({data, request, domains}) => {
    let desc = (name, value) => {
      if (name == 'published') {
        return <Item.Description key={name}>In: {value}</Item.Description>
      }
      return <Item.Description key={name}>{value}</Item.Description>
    }
    let fields = ['subtitle', 'author', 'published', 'origin']
    let descriptions = fields.filter(n => data.mods[n]).map(n => desc(n, data.mods[n]))
    let dname = domains.odid2name[data.odid]

    return (
      <Item key={data.otid}>
        <LargeThumb
          pageid={data.op_ot_id}
          imgheight={data.op_imgheight}
          imgwidth={data.op_imgwidth}
        />
        <Item.Content>
          <Item.Content className={styles.cut} style={{fontSize: '1.1em'}}>
            <Link request={{dname, classname: 'tree', tabname: 'viewer', id: data.otid}}>
              {data.mods.title}
            </Link>
          </Item.Content>
          {descriptions}
        </Item.Content>
      </Item>
    )
  }
)

const DayGroupedTitles = ({rows}) => {
  let byDay = groupBy(rows, e =>
    e.oc_releasedate ? formatDate(e.oc_releasedate, 'yyyy-mm-dd') : 'Unreleased'
  )
  byDay = sorted(entries(byDay), e => e[0], true)
  let days = byDay.map(day_plus_titles => {
    let [day, titles] = day_plus_titles
    let items = titles.map(e => {
      return <ListItem key={e.otid} data={e} />
    })
    return (
      <div key={day}>
        <h3>{day === 'Unreleased' ? day : formatDate(day)}</h3>
        <Item.Group divided>{items} </Item.Group>
        <Divider />
      </div>
    )
  })
  return days
}

export const ReleaseHistory = funcPart(({request, dconfig, releaseHistory, partcfg, i18n}) => {
  if (!releaseHistory) {
    return <Loader size="small" inline="centered" active />
  }

  if (size(releaseHistory) > 0) {
    if (false) {
      return <DayGroupedTitles rows={releaseHistory} />
    } else {
      return <TitleGrid data={releaseHistory} grid={!!partcfg.grid} />
    }
  } else {
    let limit = request.limit || partcfg.limit || dconfig.templating.releaseHistory.daylimit
    return <div>{i18n.noEntriesAvailableNow.replace('XNUM', limit)}</div>
  }
})

const Navigator = connect(
  'Navigator',
  'meta.request',
  ({request, navigator, onChange}) => {
    let {offset = 0} = request
    let numNavPages = parseInt(navigator.total / navigator.max)

    let onPageChange = (event, data) => {
      let offset = (data.activePage - 1) * navigator.max
      if (offset == 0) {
        offset = null
      }
      if (onChange) {
        onChange(offset)
      }
    }
    return (
      <Pagination
        attached
        activePage={Math.ceil((offset + 1) / navigator.max)}
        totalPages={numNavPages}
        onPageChange={onPageChange}
        ellipsisItem={{content: <Icon name="ellipsis horizontal" />, icon: true}}
        firstItem={{content: <Icon name="angle double left" />, icon: true}}
        lastItem={{content: <Icon name="angle double right" />, icon: true}}
        prevItem={{content: <Icon name="angle left" />, icon: true}}
        nextItem={{content: <Icon name="angle right" />, icon: true}}
      />
    )
  }
)

export class AllDocuments extends Part {
  state = {
    offsetData: {},
    loadingOffset: 0,
    offsetDataKey: null,
    atBottom: false,
  }
  static className = 'attached'
  static partStyle = {overflow: 'hidden'}

  static getDerivedStateFromProps(props, state) {
    /* reset local state on request conditions
     */
    let nextState = {}

    let {dname, facets, max, offset} = props.request

    let offsetDataKey = `${dname || ''}_${facets || ''}_${max || ''}`
    if (state.offsetDataKey !== offsetDataKey) {
      nextState = {...nextState, offsetDataKey, offsetData: {}}
      if (state.contextRef) {
        state.contextRef.scrollTop = 0
      }
    }

    if (state.loadingOffset && state.loadingOffset === offset) {
      nextState = {...nextState, loadingOffset: 0}
    }

    return nextState
  }

  handleContextRef = contextRef => this.setState({contextRef})

  handleScroll = e => {
    let {allDocuments, go, navigator} = this.props
    let {offsetData, loadingOffset, atBottom} = this.state

    let {scrollHeight, scrollTop, clientHeight} = e.target
    let bottomDelta = Math.abs(scrollHeight - scrollTop - clientHeight)
    if (atBottom && bottomDelta > 50) {
      this.setState({atBottom: false})
    } else if (!atBottom && bottomDelta < 5) {
      let offset = navigator.offset || 0
      let nextOffset = offset + navigator.max

      if (nextOffset < navigator.total && nextOffset !== loadingOffset) {
        //~ log('bottom %o', {loadingOffset, bottomDelta})
        this.setState({
          offsetData: {...offsetData, [offset]: allDocuments},
          loadingOffset: nextOffset,
          atBottom: true,
        })
        go({updateQuery: {offset: nextOffset}}, {history: 'replace'})
      }
    }
  }

  onNavigator = offset => {
    this.props.go({updateQuery: {offset}}, {history: 'replace'})
    this.setState({offsetData: {}, loadingOffset: 0})
    let {contextRef} = this.state
    if (contextRef) {
      contextRef.scrollTop = 0
    }
  }

  _render() {
    let {allDocuments = [], navigator, facets, request} = this.props
    let {offsetData, loadingOffset} = this.state

    let buildItems = allDocs => allDocs.map(e => <ListItem key={e.otid} data={e} />)

    const {contextRef} = this.state
    let offsets = keys(offsetData).filter(e => e != (request.offset || 0))
    let oldAllDocs = flatten(sorted(offsets).map(o => buildItems(offsetData[o])))
    let allItems = [...oldAllDocs, buildItems(allDocuments)]

    if (loadingOffset) {
      allItems.push(
        <Item key="loader" size="tiny">
          <Loader active inline="centered" />
        </Item>
      )
    }
    let flex = {
      //~ width: '100%',
      height: '100%',
    }

    let CSS_MAGIC = 97
    return (
      <>
        <Navigator navigator={navigator} onChange={this.onNavigator} />

        <div
          ref={this.handleContextRef}
          style={{...flex, overflowY: 'auto', overflowX: 'hidden'}}
          onScroll={this.handleScroll}
        >
          <Rail
            internal
            position="right"
            style={{marginRight: 'var(--vl_scrollbar_width)', paddingRight: 0}}
          >
            <Sticky context={contextRef} offset={CSS_MAGIC}>
              <Facets facets={facets} />
            </Sticky>
          </Rail>
          <Segment attached style={{flex: 1}}>
            <Item.Group divided>{allItems}</Item.Group>
          </Segment>
        </div>
      </>
    )
  }
}

export class TestNavbox extends Part {
  static storeMap = (root, props) => ({
    ...Part.selectPartData(root, props),
    dname2ddata: root.config.domains.data,
  })

  _render() {
    let {navboxData, dname, dname2ddata} = this.props

    let {menuboxes} = this.props.dconfig.templating
    //~ this.pprint(menuboxes)

    let buildDomain = item => (
      <Menu.Item
        key={`domain_${item.name}`}
        as="a"
        name={dname2ddata[item.name].description}
        active={item.name === dname}
        onClick={() => this.props.go({dname: item.name, id: null})}
      />
    )

    let {domains} = navboxData

    let items = domains.map(item => buildDomain(item))

    let rootNode = (
      <>
        <Menu pointing secondary vertical>
          <BoxHeaderIcon name="Domains" />
          {items}
        </Menu>
      </>
    )

    return rootNode
  }
}
